import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, ClipboardCheck, LineChart, MonitorPlay } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser, login } from "../../../utils/auth";
import { isPlatformHost } from "../../../utils/brandHost";
import { themeTokenValues } from "../../../utils/applyBrandTokens";
import { useBranding } from "../../../contexts/BrandingContext";
import { useTheme } from "../../../contexts/ThemeContext";
import Shimmer from "../../Common/Shimmer";
import AuthLayout from "../AuthLayout";
import { headlineNode } from "../AuthBrandSide";
import JoinForm from "./JoinForm";
import InviteStatus from "./InviteStatus";
import { MOVED_PARAM, inviteCodeFrom, moveTarget, signedInAs, statusOf } from "./joinInvite";

/**
 * `/join?code=…` — where a student lands from the institute's invite link
 * (Settings → Student Invite Link), on AuthLayout like Login and Signup.
 *
 * ── Whose brand it wears ─────────────────────────────────────────────────────
 * The link is built on the institute's own address, and an older link opened
 * on our host is MOVED there (`moveTarget`) — so in production this page is
 * almost always on the institute's subdomain or custom domain, and the host's
 * branding (BrandingContext) is the right one.
 *
 * When it cannot move — a developer's machine, or an institute with no address
 * of its own — it is on our host, and wears the brand the invite check
 * returned instead: name, mark, login-page panel, and the institute's app
 * theme as custom properties on a wrapper, so `--primary` and friends follow
 * it for this page only. Until that answer arrives the brand slots are empty,
 * never ours.
 */

const JOIN_HIGHLIGHTS = [
  { icon: MonitorPlay, title: "Live classes", desc: "Join from your phone or laptop" },
  { icon: BookOpen, title: "Course material", desc: "Notes, videos and recordings" },
  { icon: ClipboardCheck, title: "Tests & practice", desc: "See your results straight away" },
  { icon: LineChart, title: "Your progress", desc: "Attendance, scores, certificates" },
];

const PENDING_BRAND = { name: "", favicon: null, subtitle: "", pending: true };

const SIGN_IN_LINK = { to: "/login", label: "Sign In" };

/** The card while the invite is checked (or the browser is moving on). */
const JoinSkeleton = ({ label }) => (
  <div aria-busy="true">
    <span className="tw-sr-only" role="status">
      {label}
    </span>
    <div aria-hidden="true">
      <div className="tw-mx-auto tw-mb-3 tw-w-36">
        <Shimmer height={24} />
      </div>
      <div className="tw-mx-auto tw-mb-2 tw-w-3/4">
        <Shimmer height={32} />
      </div>
      <div className="tw-mx-auto tw-mb-7 tw-w-2/3">
        <Shimmer height={16} />
      </div>
      <div className="tw-flex tw-flex-col tw-gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <div className="tw-mb-1.5 tw-w-28">
              <Shimmer height={14} />
            </div>
            <Shimmer height={44} />
          </div>
        ))}
        <Shimmer height={48} />
      </div>
    </div>
  </div>
);

const JoinInstitutePage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const branding = useBranding();
  const { theme } = useTheme();
  const code = inviteCodeFrom(search);

  // phase: checking | moving | ready | status
  const [view, setView] = useState(() => (code ? { phase: "checking" } : { phase: "status", status: "INVITE_MISSING" }));
  // The invite check's answer: brandName, joinUrl, branding — on success, and on
  // a refusal whose institute is known.
  const [invite, setInvite] = useState(null);
  // Read once: joining replaces this session, so the form says so up front.
  const [signedIn] = useState(() => signedInAs(getCurrentUser(), localStorage.getItem("token")));
  const latest = useRef(0);

  const check = useCallback(async () => {
    const seq = ++latest.current;
    if (!code) {
      setInvite(null);
      setView({ phase: "status", status: "INVITE_MISSING" });
      return;
    }
    setView({ phase: "checking" });

    let data = null;
    let refused = false;
    try {
      ({ data } = await apiClient.get("/auth/invite/validate", { params: { code } }));
    } catch (err) {
      if (seq !== latest.current) return;
      if (!err?.response) {
        setInvite(null);
        setView({ phase: "status", status: "NETWORK" });
        return;
      }
      refused = true;
      data = err.response.data || {};
    }
    if (seq !== latest.current) return;

    const target = moveTarget(data?.joinUrl, window.location);
    if (target) {
      setView({ phase: "moving" });
      window.location.replace(target);
      return;
    }
    setInvite(data?.brandName ? data : null);
    setView(refused ? { phase: "status", status: statusOf(data?.code) } : { phase: "ready" });
  }, [code]);

  useEffect(() => {
    check();
  }, [check]);

  // The one-hop marker has done its job once the page decides to stay; keep it
  // out of a link the student might copy from the address bar.
  useEffect(() => {
    if (view.phase === "checking" || view.phase === "moving") return;
    const params = new URLSearchParams(search);
    if (!params.has(MOVED_PARAM)) return;
    params.delete(MOVED_PARAM);
    navigate({ search: `?${params}` }, { replace: true });
  }, [view.phase, search, navigate]);

  const handleJoined = (data) => {
    // A server that predates the session-user fix answers without `usertype`,
    // and ProtectedRoute would bounce that session to "Not authorized". The
    // account exists either way, so send the student to sign in instead.
    if (!data?.token || !data?.user?.usertype) {
      setView({ phase: "status", status: "ACCOUNT_CREATED" });
      return;
    }
    login(data);
    branding.refreshBranding?.();
    navigate("/s/dashboard", { replace: true });
  };

  // ── Whose brand ─────────────────────────────────────────────────────────────
  const onOurHost = isPlatformHost(window.location.hostname);
  const inviteBrand = onOurHost && invite?.branding ? invite.branding : null;
  let brand; // undefined → the host's brand, from BrandingContext
  if (inviteBrand) {
    brand = {
      name: inviteBrand.brandName || invite.brandName,
      favicon: inviteBrand.faviconUrl || inviteBrand.logoUrl,
      subtitle: inviteBrand.brandSubtitle,
    };
  } else if (onOurHost && (view.phase === "checking" || view.phase === "moving")) {
    brand = PENDING_BRAND;
  }

  const tokenStyle = useMemo(
    () => (inviteBrand ? themeTokenValues(inviteBrand.appTheme, theme) : undefined),
    [inviteBrand, theme]
  );

  const brandName = invite?.brandName || brand?.name || branding.brandName || "";

  const side = {
    // The institute's own panel when it has one, on our host as on its own.
    loginPage: inviteBrand ? inviteBrand.loginPage || null : undefined,
    title: headlineNode("You're invited to\nlearn with", brandName || "your institute"),
    blurb: "Create your student account to reach your classes, course material and tests — all in one place.",
    highlights: JOIN_HIGHLIGHTS,
    footnote: "",
  };

  let card;
  if (view.phase === "checking" || view.phase === "moving") {
    card = (
      <JoinSkeleton
        label={view.phase === "moving" ? "Opening your institute's page…" : "Checking your invite…"}
      />
    );
  } else if (view.phase === "ready") {
    card = (
      <JoinForm
        code={code}
        brandName={brandName}
        signedIn={signedIn}
        onJoined={handleJoined}
        onRefused={(status) => setView({ phase: "status", status })}
        onDashboard={() => navigate("/s/dashboard")}
      />
    );
  } else {
    card = (
      <InviteStatus
        status={view.status}
        brandName={invite?.brandName}
        onRetry={check}
        onCode={(next) => navigate(`/join?code=${encodeURIComponent(next)}`)}
      />
    );
  }

  return (
    <div style={tokenStyle}>
      <AuthLayout brand={brand} side={side} mobileLink={SIGN_IN_LINK}>
        {card}
      </AuthLayout>
    </div>
  );
};

export default JoinInstitutePage;
